import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default shipper address (your warehouse/store address)
const DEFAULT_SHIPPER_ADDRESS = {
  name: "Arihant Milk Products",
  email: "contact@arihantmilk.com",
  phone: "9876543210",
  address_line1: "Industrial Area, Sector 5",
  address_line2: "Near Main Market",
  city: "Delhi",
  state: "Delhi",
  postal_code: "110001",
  country: "India"
};

export const shipmentService = {
  /**
   * Create a shipment automatically after order placement
   */
  createShipmentFromOrder: async (order, shippingInfo) => {
    try {
      console.log('🚚 Starting shipment creation for order:', order.id);
      console.log('Order data:', order);
      console.log('Shipping info:', shippingInfo);

      // Validate required data
      if (!order || !order.id) {
        throw new Error('Invalid order data - missing order ID');
      }

      if (!shippingInfo || !shippingInfo.phone || !shippingInfo.address || !shippingInfo.pincode) {
        throw new Error('Invalid shipping info - missing required fields');
      }

      // Prepare products with weight information
      const products = order.items.map(item => {
        // Extract weight - handle various formats
        let weight = 0.5; // default
        if (item.weight) {
          const weightStr = String(item.weight);
          // Extract numbers from weight string (e.g., "500ml" -> "500", "1kg" -> "1")
          const numMatch = weightStr.match(/[\d.]+/);
          if (numMatch) {
            weight = parseFloat(numMatch[0]);
            // If it's in grams/ml (> 50), convert to kg
            if (weight > 50) {
              weight = weight / 1000;
            }
          }
        }

        return {
          name: item.name || 'Product',
          quantity: item.quantity || 1,
          weight: weight,
          price: item.price || 0
        };
      });

      console.log('Prepared products:', products);

      // Prepare consignee address from shipping info
      const consigneeAddress = {
        name: shippingInfo.fullName || 'Customer',
        email: shippingInfo.email || '',
        phone: shippingInfo.phone,
        address_line1: shippingInfo.address,
        address_line2: '',
        city: shippingInfo.city || 'Delhi',
        state: shippingInfo.state || 'Delhi',
        postal_code: shippingInfo.pincode,
        country: "India"
      };

      console.log('Consignee address:', consigneeAddress);

      // Determine payment mode and COD amount
      const paymentMode = order.paymentMethod === 'cod' ? 'COD' : 'PREPAID';
      const codAmount = order.paymentMethod === 'cod' ? order.totalAmount : 0;

      console.log('Payment mode:', paymentMode, 'COD Amount:', codAmount);

      // Create shipment request
      const shipmentData = {
        order_id: order.id,
        products: products,
        shipper_address: DEFAULT_SHIPPER_ADDRESS,
        consignee_address: consigneeAddress,
        payment_mode: paymentMode,
        cod_amount: codAmount,
        delivery_mode: "SURFACE",
        special_instructions: `Order #${order.id}`
      };

      console.log('Sending shipment request:', shipmentData);
      console.log('Backend URL:', BACKEND_URL);

      const response = await axios.post(
        `${BACKEND_URL}/api/shipments/create`,
        shipmentData,
        { 
          withCredentials: true,
          timeout: 30000 // 30 second timeout
        }
      );

      console.log('✅ Shipment created successfully:', response.data);

      if (!response.data.waybill_number) {
        console.warn('⚠️ Warning: No waybill number in response');
      }

      return response.data;
    } catch (error) {
      console.error('❌ Shipment creation error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    }
  },

  /**
   * Track a shipment by waybill number
   */
  trackShipment: async (waybillNumber) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/shipments/track/${waybillNumber}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Tracking error:', error);
      throw error;
    }
  },

  /**
   * Calculate shipping rate
   */
  calculateRate: async (pickupPincode, deliveryPincode, weight) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/shipments/calculate-rate`,
        {
          pickup_pincode: pickupPincode,
          delivery_pincode: deliveryPincode,
          weight: weight,
          delivery_mode: "SURFACE"
        },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Rate calculation error:', error);
      throw error;
    }
  },

  /**
   * Check if a pincode is serviceable
   */
  checkServiceability: async (pincode) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/shipments/check-serviceability/${pincode}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Serviceability check error:', error);
      throw error;
    }
  },

  /**
   * Get shipments for an order
   */
  getShipmentsByOrder: async (orderId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/shipments/order/${orderId}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('Get shipments error:', error);
      throw error;
    }
  },

  /**
   * List all shipments (admin only)
   */
  listAllShipments: async (skip = 0, limit = 50) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/shipments/list?skip=${skip}&limit=${limit}`,
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      console.error('List shipments error:', error);
      throw error;
    }
  }
};
